import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { set_notification_variable } from '../../../redux/action_creators/home_actions';
import { get_api, post_api } from '../../../redux/api_funcs';
import { get_api_url } from '../../../utils/urls';
import { getAuthUserInfoOrDefault } from '../../../myt_chat/network/cookie';
import { CallbackHandler } from '../../../myt_chat/chat/callbacks/handler';
import {
  getOutgoingCallerId,
  prepareOutbound,
  resolveContact,
  startCall
} from '../students/student_profile/twilioCall';
import OutboundCallPill from './OutboundCallPill';
import { unlockTwilioAudio } from './twilio_audio';
import { discardPendingDiag, flushClientDiag } from './call_diagnostics';

const getUrl = (endpoint) => {
  if (import.meta.env.VITE_TWILIO_CALL_BASE_URL) {
    return `${import.meta.env.VITE_TWILIO_CALL_BASE_URL}${endpoint}`;
  }
  return get_api_url(endpoint);
};

const TERMINAL_STATUSES = new Set(['ended', 'disconnected', 'failed']);
const AUTO_DISMISS_MS = 2500;

// `window.__twilioOutboundActive` is derived from `active`, and `active` only
// clears when the SDK reports a terminal status. When that report never
// arrives — dead socket, laptop sleep/wake, token expiry mid-call, a tab the
// browser discarded — the flag pins `true` for the life of the page and
// GlobalIncomingCallManager auto-rejects every incoming call as busy. The
// watchdog below is the backstop: it reconciles our state against the SDK's
// and forces terminal when they disagree.
const WATCHDOG_INTERVAL_MS = 15 * 1000;
// Twilio Call.status() values that mean the call is no longer live. The SDK's
// live states are pending | connecting | ringing | open | reconnecting.
const SDK_CLOSED_STATUSES = new Set(['closed']);
// A call that never reaches 'connected' can't outlive the dial window by much
// — Twilio's own dial timeout is well under this.
const PRE_CONNECT_CEILING_MS = 2 * 60 * 1000;
// After the agent clicks End we set 'disconnecting' and wait for the SDK's
// terminal event. If it doesn't come, don't sit in that state forever.
const DISCONNECTING_CEILING_MS = 20 * 1000;
// Last-resort ceiling on a single call. No legitimate outbound runs this long;
// past it we force terminal regardless of what the SDK claims.
const MAX_CALL_MS = 4 * 60 * 60 * 1000;
// Ceiling on the pre-dial metadata lookups. These are plain HTTP requests; a
// hung (never-settling) one would otherwise leave the pill at 'connecting'
// with the flag stuck true and no error path ever taken.
const PRE_DIAL_TIMEOUT_MS = 15 * 1000;

// Reject if `promise` hasn't settled within `ms`. The timer is always cleared
// so a slow-but-successful lookup doesn't leave a pending timeout behind. The
// message is agent-facing — it lands in the error toast via startOutbound's
// catch — so it says what to do, not how long we waited.
const withTimeout = (promise, ms, label) => {
  let timer = null;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      // eslint-disable-next-line no-console
      console.warn('global_outbound.pre_dial_timeout', { label, timeoutMs: ms });
      reject(new Error(`${label} timed out — check your connection and try again.`));
    }, ms);
  });
  return Promise.race([promise, timeout]).finally(() => {
    if (timer) clearTimeout(timer);
  });
};

// Mounted once in the home shell next to GlobalIncomingCallManager. Owns
// the active outbound call's lifecycle so it survives page navigation —
// previously the call lived inside per-page components (StudentProfile,
// TwilioAgent dialer-pad) and any nav unmounted them, killing the call.
//
// Pages don't store call state anymore. They dispatch:
//   - twilio-outgoing-call-start  { studentUuid?, studentName?, toNumber? }
// And we broadcast:
//   - twilio-outgoing-call-active { active: bool }
// pages listen for this to disable their Call buttons during a call.
//
// We also expose `window.__twilioOutboundActive` as an idempotent flag for
// late-mounted listeners that need the current state without waiting for
// the next event.
function GlobalOutboundCallManager() {
  const dispatch = useDispatch();
  // Pill state. Null when no call is in progress.
  const [active, setActive] = useState(null);
  // Twilio call controller returned by startCall. Set after the SDK
  // Device has been created and connected.
  const callHandleRef = useRef(null);
  // Synchronous in-flight latch. Flipped to true *before* any await so two
  // rapid Call clicks can't both pass the callHandleRef-only guard.
  const dialingRef = useRef(false);
  const autoDismissTimerRef = useRef(null);
  // Wall-clock start of the current call cycle, and the moment we entered
  // 'disconnecting'. Both feed the watchdog's ceilings; null when idle.
  const callStartedAtRef = useRef(null);
  const disconnectingSinceRef = useRef(null);
  // Mirror of the last value we broadcast, so unmount can tell whether it is
  // actually stranding a live flag without re-reading window state.
  const flagRef = useRef(false);
  // Bumped every time a call cycle is torn down. startOutbound captures it
  // before its awaits, so a dial we already gave up on (watchdog ceiling, user
  // dismiss) can't install its controller when it finally resolves — that
  // would leave callHandleRef non-null with no active call and block every
  // later dial on the "already in progress" guard.
  const dialEpochRef = useRef(0);

  // Conference state for the outbound call.
  const [activeCallUUID, setActiveCallUUID] = useState(null);
  // Non-null while a media problem is active on this leg.
  const [audioWarning, setAudioWarning] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [unlocked, setUnlocked] = useState(false);
  // care_uuid is a shared constant for the care chat user, not the signed-in
  // agent; identity must come from the auth token.
  const currentUserUUID = getAuthUserInfoOrDefault().uuid || '';

  // Mirror activeCallUUID into a ref so the relay handler — which is
  // registered once per component lifetime — always compares against the
  // latest value. Without this, an `unlocked` or `participant_changed`
  // event arriving before the next React render (or before the lookup
  // completes) could be dropped because the stale closure still has
  // activeCallUUID === null.
  const activeCallUUIDRef = useRef(null);
  useEffect(() => {
    activeCallUUIDRef.current = activeCallUUID;
    // The SDK leg exists before the uuid does, so diag events raised in
    // between were buffered; this is the moment they can be attributed.
    if (activeCallUUID) flushClientDiag(activeCallUUID);
  }, [activeCallUUID]);

  const broadcastActive = useCallback((isActive) => {
    if (typeof window !== 'undefined') {
      window.__twilioOutboundActive = isActive;
      flagRef.current = isActive;
      window.dispatchEvent(
        new CustomEvent('twilio-outgoing-call-active', { detail: { active: isActive } })
      );
    }
  }, []);

  const clearAutoDismissTimer = useCallback(() => {
    if (autoDismissTimerRef.current) {
      clearTimeout(autoDismissTimerRef.current);
      autoDismissTimerRef.current = null;
    }
  }, []);

  const closePill = useCallback(() => {
    clearAutoDismissTimer();
    dialEpochRef.current += 1;
    callHandleRef.current = null;
    callStartedAtRef.current = null;
    disconnectingSinceRef.current = null;
    setActive(null);
    setActiveCallUUID(null);
    setParticipants([]);
    setUnlocked(false);
    setAudioWarning(null);
    discardPendingDiag();
    broadcastActive(false);
  }, [broadcastActive, clearAutoDismissTimer]);

  // Tear the call down when our state and the SDK's have diverged, or a
  // ceiling was hit. Same teardown as closePill, plus a best-effort release of
  // the SDK Device (whose own cleanup runs off the terminal events that, by
  // definition, didn't fire here) and a structured log so these are
  // distinguishable from a normal user dismiss in Sentry/console.
  const forceTerminal = useCallback(
    (reason, detail = {}) => {
      // eslint-disable-next-line no-console
      console.warn('global_outbound.watchdog_forced_terminal', { reason, ...detail });
      try {
        callHandleRef.current?.teardown?.();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('global_outbound.forced_terminal_teardown_failed', e);
      }
      closePill();
    },
    [closePill]
  );

  const scheduleAutoDismiss = useCallback(() => {
    clearAutoDismissTimer();
    autoDismissTimerRef.current = setTimeout(() => {
      setActive((prev) => {
        if (prev && TERMINAL_STATUSES.has(prev.status)) {
          callHandleRef.current = null;
          broadcastActive(false);
          return null;
        }
        return prev;
      });
      autoDismissTimerRef.current = null;
    }, AUTO_DISMISS_MS);
  }, [broadcastActive, clearAutoDismissTimer]);

  const startOutbound = useCallback(
    async (detail) => {
      // Runs in the click's user-activation window: the Call buttons dispatch
      // `twilio-outgoing-call-start` synchronously, and Safari honours a
      // resume() only from inside that window — everything below awaits.
      unlockTwilioAudio();

      const studentUuid = (detail?.studentUuid || '').trim();
      const studentNameInput = (detail?.studentName || '').trim();
      const toNumberInput = (detail?.toNumber || '').trim();

      if (!studentUuid && !toNumberInput) {
        dispatch(
          set_notification_variable(true, 'error', 'Missing phone number or student to call')
        );
        return;
      }

      if (dialingRef.current || callHandleRef.current) {
        dispatch(set_notification_variable(true, 'error', 'A call is already in progress'));
        return;
      }

      // Block outbound while an incoming call is ringing or connected. The
      // global incoming manager flips this flag the same way we flip the
      // outbound one; backend Redis busy keys protect at the server level
      // either way, but blocking here gives the agent immediate feedback.
      if (typeof window !== 'undefined' && window.__twilioIncomingActive) {
        dispatch(set_notification_variable(true, 'error', 'Finish the incoming call first'));
        return;
      }

      dialingRef.current = true;
      callStartedAtRef.current = Date.now();
      disconnectingSinceRef.current = null;
      const dialEpoch = dialEpochRef.current;
      setActive({
        studentUuid,
        studentName: studentNameInput,
        toNumber: toNumberInput,
        status: 'connecting',
        isMuted: false
      });
      broadcastActive(true);

      try {
        let toNumber = toNumberInput;
        let fromNumber = '';
        let resolvedStudentUuid = studentUuid;
        let resolvedName = studentNameInput;

        if (studentUuid) {
          // Student-profile path: phone + caller-id from the student record.
          const [contact, fromNum] = await withTimeout(
            Promise.all([
              toNumberInput ? Promise.resolve(null) : resolveContact(studentUuid),
              getOutgoingCallerId(studentUuid)
            ]),
            PRE_DIAL_TIMEOUT_MS,
            'Contact lookup'
          );
          if (contact) toNumber = contact.to_number;
          fromNumber = fromNum;
        } else {
          // Dialer-pad path: phone-keyed lookup that also returns a
          // best-effort student_uuid + name when phone_personal matches.
          const info = await withTimeout(
            prepareOutbound(toNumberInput),
            PRE_DIAL_TIMEOUT_MS,
            'Outbound preparation'
          );
          if (!info?.to_number) throw new Error('Failed to prepare outbound call');
          toNumber = info.to_number;
          fromNumber = info.from_number;
          resolvedStudentUuid = info.student_uuid || '';
          resolvedName = info.name || '';
        }

        if (!toNumber) throw new Error('No phone number to dial');

        setActive((prev) =>
          prev
            ? {
                ...prev,
                toNumber,
                studentUuid: resolvedStudentUuid,
                studentName: resolvedName || prev.studentName
              }
            : prev
        );

        const ctrl = await startCall({
          toNumber,
          fromNumber,
          studentUuid: resolvedStudentUuid,
          onStatus: (status) => {
            if (dialEpochRef.current !== dialEpoch) return;
            setActive((prev) => (prev ? { ...prev, status } : prev));
            if (TERMINAL_STATUSES.has(status)) {
              callHandleRef.current = null;
              setAudioWarning(null);
              scheduleAutoDismiss();
            }
          },
          onAudioWarning: (message) => {
            if (dialEpochRef.current !== dialEpoch) return;
            setAudioWarning(message || null);
            if (message) dispatch(set_notification_variable(true, 'error', message));
          },
          getCallUuid: () => activeCallUUIDRef.current,
          onMuteChange: (isMuted) => {
            if (dialEpochRef.current !== dialEpoch) return;
            setActive((prev) => (prev ? { ...prev, isMuted: !!isMuted } : prev));
          }
        });
        // This dial was abandoned while startCall was still resolving — don't
        // adopt its controller, just release it.
        if (dialEpochRef.current !== dialEpoch) {
          // eslint-disable-next-line no-console
          console.warn('global_outbound.discarded_stale_dial', { dialEpoch });
          try {
            ctrl?.teardown?.();
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error('global_outbound.stale_dial_teardown_failed', e);
          }
          return;
        }
        callHandleRef.current = ctrl;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('outbound dial failed', err);
        // A dial we already abandoned must not tear down whatever call cycle
        // is live now, nor toast an error for a call the agent has moved on
        // from.
        if (dialEpochRef.current !== dialEpoch) return;
        dispatch(
          set_notification_variable(
            true,
            'error',
            err?.response?.data?.reason || err?.message || 'Failed to place call'
          )
        );
        callHandleRef.current = null;
        callStartedAtRef.current = null;
        disconnectingSinceRef.current = null;
        setActive(null);
        broadcastActive(false);
      } finally {
        dialingRef.current = false;
      }
    },
    [broadcastActive, dispatch, scheduleAutoDismiss]
  );

  useEffect(() => {
    const onStart = (e) => startOutbound(e?.detail || {});
    window.addEventListener('twilio-outgoing-call-start', onStart);
    return () => window.removeEventListener('twilio-outgoing-call-start', onStart);
  }, [startOutbound]);

  // Sync the window flag for late-mounted listeners (e.g. a student profile
  // page mounted *after* a call already started).
  useEffect(() => {
    broadcastActive(!!active);
  }, [active, broadcastActive]);

  // Watchdog. Armed only while a call is live (one interval, cleared as soon
  // as `active` goes null), it asks the SDK what it thinks the call's state is
  // and forces terminal when that disagrees with ours or a ceiling is hit.
  // Re-arms on each status transition, which is a handful of times per call.
  useEffect(() => {
    if (!active) return undefined;

    const id = setInterval(() => {
      const { status } = active;
      const startedAt = callStartedAtRef.current;
      const elapsedMs = startedAt ? Date.now() - startedAt : 0;

      // Already terminal — scheduleAutoDismiss owns this one.
      if (TERMINAL_STATUSES.has(status)) return;

      if (startedAt && elapsedMs >= MAX_CALL_MS) {
        forceTerminal('max_duration_exceeded', { status, elapsedMs });
        return;
      }

      // The SDK is the source of truth: if it says closed, our non-terminal
      // status means a terminal event was dropped.
      const sdkStatus = callHandleRef.current?.getStatus?.() ?? null;
      if (sdkStatus && SDK_CLOSED_STATUSES.has(sdkStatus)) {
        forceTerminal('sdk_call_closed', { status, sdkStatus, elapsedMs });
        return;
      }

      if (status === 'disconnecting') {
        const since = disconnectingSinceRef.current;
        if (since && Date.now() - since >= DISCONNECTING_CEILING_MS) {
          forceTerminal('disconnect_not_acknowledged', {
            status,
            sdkStatus,
            disconnectingMs: Date.now() - since
          });
        }
        return;
      }

      // Never reached 'connected' and the dial window is long gone — covers a
      // startCall() that never settled, so callHandleRef is still null and
      // there's no SDK object to interrogate.
      if (status !== 'connected' && startedAt && elapsedMs >= PRE_CONNECT_CEILING_MS) {
        forceTerminal('stuck_before_connect', { status, sdkStatus, elapsedMs });
      }
    }, WATCHDOG_INTERVAL_MS);

    return () => clearInterval(id);
  }, [active, forceTerminal]);

  // On unmount, release the flag. Without this an unmount while a call is
  // still `active` (shell remount, error boundary, HMR, logout without a full
  // reload) strands `window.__twilioOutboundActive === true` with no live
  // component able to ever clear it — unrecoverable short of a page reload.
  useEffect(
    () => () => {
      clearAutoDismissTimer();
      if (flagRef.current) {
        // eslint-disable-next-line no-console
        console.warn('global_outbound.flag_cleared_on_unmount');
      }
      broadcastActive(false);
    },
    [broadcastActive, clearAutoDismissTimer]
  );

  // Look up the outbound row in /ongoing_calls and resolve its call_uuid
  // (the Twilio SDK doesn't expose it client-side). The originator's row
  // is the one whose ops_user_uuid matches the current user. Used both by
  // the connected-time effect below AND as a fallback when the unlock
  // click fires before the effect's lookup completes.
  const lookupActiveCallUUID = useCallback(async () => {
    if (!currentUserUUID) return null;
    try {
      const resp = await get_api(getUrl('/v2/twilio_call/ongoing_calls'), true);
      const calls = resp?.data?.data?.calls || [];
      const own = calls.find(
        (c) =>
          (c.direction || '').toLowerCase() === 'outgoing' && c.ops_user_uuid === currentUserUUID
      );
      if (own?.uuid) {
        setActiveCallUUID(own.uuid);
        if (own.conference_sid) setUnlocked(true);
        return own.uuid;
      }
      return null;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('global_outbound.lookup_call_uuid_failed', err);
      return null;
    }
  }, [currentUserUUID]);

  // Once an outbound call connects, do the lookup once per cycle.
  useEffect(() => {
    if (!active || active.status !== 'connected' || activeCallUUID || !currentUserUUID)
      return undefined;
    let cancelled = false;
    (async () => {
      const uuid = await lookupActiveCallUUID();
      if (cancelled || !uuid) {
        // Retry once after a short delay — the row exists once Twilio's
        // /voice handler returns, but the very first attempt can race the
        // server-side commit + cache fill on a cold path.
        setTimeout(() => {
          if (!cancelled) lookupActiveCallUUID();
        }, 1500);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active, activeCallUUID, currentUserUUID, lookupActiveCallUUID]);

  // Subscribe to the conference relays so the pill flips to unlocked / shows
  // accurate participant count without a manual refresh. Also picks up the
  // `outbound_call_started` relay that voiceOutgoing fires the moment the
  // row is created — this is the primary path for resolving call_uuid;
  // the /ongoing_calls lookup above is a fallback for missed events.
  //
  // Registered once per component lifetime (no deps). The stale-closure
  // problem is avoided by reading `activeCallUUIDRef.current` instead of
  // the `activeCallUUID` state value, AND by *also* accepting `unlocked`
  // / `participant_changed` events whose call_uuid matches the current
  // active outbound — even when activeCallUUID hasn't been resolved yet —
  // so an early `unlocked` (e.g. originator double-clicked unlock fast)
  // doesn't get dropped.
  useEffect(() => {
    const handler = (item) => {
      if (!item || !item.data) return;
      const { type } = item.data;
      if (type === 'outbound_call_started' && item.data.call_uuid) {
        setActiveCallUUID(item.data.call_uuid);
        return;
      }

      const incomingUUID = item.data.call_uuid;
      if (!incomingUUID) return;
      const knownUUID = activeCallUUIDRef.current;

      if (type === 'unlocked') {
        // Match the current call_uuid if known. If we don't yet know it
        // (lookup hasn't completed), adopt this UUID as our active call —
        // outbound_call_started usually arrives first, but if we missed
        // it the unlocked event still gives us a valid anchor.
        if (knownUUID && incomingUUID !== knownUUID) return;
        if (!knownUUID) {
          setActiveCallUUID(incomingUUID);
        }
        setUnlocked(true);
      } else if (type === 'participant_changed') {
        if (knownUUID && incomingUUID !== knownUUID) return;
        if (!knownUUID) {
          setActiveCallUUID(incomingUUID);
        }
        setParticipants(Array.isArray(item.data.participants) ? item.data.participants : []);
      }
    };
    CallbackHandler.shared.registerRelayItemListener(handler);
    return () => CallbackHandler.shared.unregisterRelayItemListener(handler);
  }, []);

  const handleEndCall = useCallback(() => {
    // Stamped before the hangup so the watchdog can bound how long we're
    // willing to sit in 'disconnecting' waiting for the SDK's terminal event.
    disconnectingSinceRef.current = Date.now();
    setActive((prev) => (prev ? { ...prev, status: 'disconnecting' } : prev));
    try {
      callHandleRef.current?.hangup?.();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('outbound hangup error', e);
      // hangup() threw, so the SDK will never emit 'disconnect' and nothing
      // else would move us off 'disconnecting' — the flag would pin true.
      // Force it here rather than waiting out the watchdog's ceiling.
      forceTerminal('hangup_threw', { error: e?.message || String(e) });
    }
  }, [forceTerminal]);

  const handleToggleMute = useCallback(() => {
    try {
      callHandleRef.current?.toggleMute?.();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('outbound mute error', e);
    }
  }, []);

  const handleUnlock = useCallback(async () => {
    // The connected-time lookup is best-effort and can race the row's
    // server-side commit on a cold path. If we don't have the call_uuid
    // yet, do the lookup inline rather than swallowing the click.
    let callUUID = activeCallUUID;
    if (!callUUID) {
      callUUID = await lookupActiveCallUUID();
    }
    if (!callUUID) {
      dispatch(
        set_notification_variable(
          true,
          'error',
          'Could not resolve this call yet — try again in a moment.'
        )
      );
      return;
    }
    try {
      await post_api(getUrl('/v2/twilio_call/unlock_call'), { call_uuid: callUUID }, true);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('outbound unlock failed', err);
      const reason =
        err?.response?.data?.reason || err?.message || 'Failed to move call to conference';
      dispatch(set_notification_variable(true, 'error', reason));
    }
  }, [activeCallUUID, dispatch, lookupActiveCallUUID]);

  const handleLeave = useCallback(() => {
    try {
      callHandleRef.current?.hangup?.();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('outbound leave error', e);
    }
  }, []);

  const handleEndForEveryone = useCallback(async () => {
    // Pre-conference state (no activeCallUUID): there's nothing for the
    // server to "end for everyone" yet — the call is still a 2-party Dial
    // bridge — so a local hangup IS the correct action and is equivalent
    // to clicking End. Fall through.
    if (!activeCallUUID) {
      try {
        callHandleRef.current?.hangup?.();
      } catch (e) {
        /* noop */
      }
      return;
    }
    try {
      await post_api(getUrl('/v2/twilio_call/end_ongoing'), { call_uuid: activeCallUUID }, true);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('outbound end_for_everyone failed', err);
      // DON'T fall through to a local hangup here. If the REST call
      // failed, hanging up locally would behave like Leave — only this
      // user drops, the conference continues for everyone else. That's
      // the opposite of the user's intent ("end for everyone") and is
      // misleading. Surface the error and let the user retry End or
      // explicitly click Leave if that's what they actually want.
      const reason =
        err?.response?.data?.reason ||
        err?.message ||
        'Failed to end call for everyone — try again, or click Leave to drop just yourself.';
      dispatch(set_notification_variable(true, 'error', reason));
      return;
    }
    // REST succeeded → server is REST-completing the conference. The
    // conference-end webhook will tear down all participants including
    // this leg; local hangup is a fast-path to release this user's SDK
    // immediately so the pill closes without waiting for the round-trip.
    try {
      callHandleRef.current?.hangup?.();
    } catch (e) {
      /* noop */
    }
  }, [activeCallUUID, dispatch]);

  if (!active) return null;

  const opsCount = participants.filter((p) => typeof p === 'string' && p.startsWith('cms_')).length;

  return (
    <OutboundCallPill
      studentName={active.studentName}
      toNumber={active.toNumber}
      status={active.status}
      muted={active.isMuted}
      audioWarning={audioWarning}
      unlocked={unlocked}
      isOriginator
      participantCount={opsCount || 1}
      onToggleMute={handleToggleMute}
      onEndCall={handleEndCall}
      onUnlock={handleUnlock}
      onLeave={handleLeave}
      onEndForEveryone={handleEndForEveryone}
      onClose={closePill}
    />
  );
}

export default GlobalOutboundCallManager;
